import { CalendarClock, ChevronDown, FileSignature, FlaskConical, FileText, FileUp, FolderKanban, Headset, KeyRound, Landmark, ListTodo, Loader2, Mail, MessageSquare, MoreHorizontal, Pencil, Play, Plus, RefreshCcw, Trash2, User, Users, Wrench } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { allowedTabs, resolveTab, type PermissionCheck } from '@/lib/auth/permissions'
import { useCustomer, useDeleteCustomer, useStartMeeting } from './api'
import { AccessRequestDialog } from './components/AccessRequestDialog'
import { CustomerAvatar, DeliveryBadge, StageBadge } from './components/CrmBits'
import { CrmDialogHost, type CrmDialog } from './components/CrmDialogs'
import { FinanceTab } from './components/detail/FinanceTab'
import { MeetingsTab } from './components/detail/MeetingsTab'
import { DeliveryTab, EmailsTab, FilesTab, RequestsTab } from './components/detail/MiscTabs'
import { OffersTab } from './components/detail/OffersTab'
import { OrdersTab } from './components/detail/OrdersTab'
import { OverviewTab } from './components/detail/OverviewTab'
import { CategoryBadge, CategoryPicker } from './categories'
import { NotesTab } from './components/detail/NotesTab'
import { ProjectTab } from './components/detail/ProjectTab'
import { CRM_FINANCE_PERM, CRM_FINANCE_READ_PERM, CRM_MAIL_READ_PERM, CRM_NOTE_WRITE_PERM, CRM_WRITE_PERM } from './permissions'
import type { Customer, CustomerSummary } from './types'
import { toastError, useCrm } from './ui'

const TABS = ['overview', 'notes', 'meetings', 'offers', 'orders', 'project', 'delivery', 'finance', 'emails', 'requests', 'files'] as const
type Tab = (typeof TABS)[number]
const TAB_PERMS: Partial<Record<Tab, PermissionCheck>> = { finance: CRM_FINANCE_READ_PERM, emails: CRM_MAIL_READ_PERM }

/**
 * Kapsam dışı müşteri varlığı sızdırılmasın diye API 404 döner; ekran yokluk ile yetkisizliği ayırmaz ve
 * süreli erişim talebi açmayı önerir (müşteri yoksa talep de reddedilir).
 */
function OutOfScope({ accountId }: { accountId: string }) {
  const { t } = useTranslation()
  const [requesting, setRequesting] = useState(false)
  return (
    <>
      <EmptyState
        icon={KeyRound}
        title={t('crm.access.forbiddenTitle')}
        description={t('crm.access.forbiddenHint')}
        action={
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            <Button size="sm" onClick={() => setRequesting(true)}>
              <KeyRound />
              {t('crm.access.new')}
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/customers/access">{t('crm.access.title')}</Link>
            </Button>
          </div>
        }
      />
      {requesting ? <AccessRequestDialog accountId={accountId} onClose={() => setRequesting(false)} /> : null}
    </>
  )
}

export function CustomerDetailPage() {
  const { id = '', tab } = useParams()
  const q = useCustomer(id)
  if (tab && !TABS.includes(tab as Tab)) return <Navigate to={`/customers/${id}`} replace />
  if (q.isError) {
    return isApiError(q.error) && q.error.status === 404 ? <OutOfScope accountId={id} /> : <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  }
  if (!q.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    )
  }
  return <Detail customer={q.data.customer} summary={q.data.summary} tab={(tab as Tab | undefined) ?? 'overview'} />
}

function Detail({ customer: c, summary, tab: requested }: { customer: Customer; summary: CustomerSummary; tab: Tab }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const tabs = allowedTabs(TABS, TAB_PERMS, can)
  const tab = resolveTab(requested, tabs)
  const navigate = useNavigate()
  const remove = useDeleteCustomer()
  const { staffName, projectName } = useCrm()
  const [dialog, setDialog] = useState<CrmDialog | null>(null)
  const canWrite = can(CRM_WRITE_PERM)
  const canPay = can(CRM_FINANCE_PERM)
  const go = (next: string) => navigate(next === 'overview' ? `/customers/${c.id}` : `/customers/${c.id}/${next}`)

  const start = useStartMeeting()
  const onStartMeeting = async () => {
    try {
      const meetingId = await start.mutateAsync({ customerId: c.id, staffId: c.ownerId, summary: t('crm.live.autoSummary') })
      navigate(`/customers/${c.id}/live/${meetingId}`)
    } catch (e) {
      toastError(e)
    }
  }
  const onDelete = async () => {
    if (!(await confirmModal({ title: t('crm.deleteTitle'), description: t('crm.deleteBody', { name: c.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    try {
      await remove.mutateAsync(c.id)
      toast.success(t('crm.deleted'))
      navigate('/customers/list', { replace: true })
    } catch (e) {
      toastError(e)
    }
  }

  const activity: { key: string; icon: typeof Plus; dialog: CrmDialog; tab: Tab; show: boolean }[] = [
    { key: 'meeting', icon: MessageSquare, dialog: { kind: 'meeting', meeting: null }, tab: 'meetings', show: canWrite },
    { key: 'visit', icon: CalendarClock, dialog: { kind: 'meeting', meeting: null, plan: true }, tab: 'meetings', show: canWrite },
    { key: 'offer', icon: FileSignature, dialog: { kind: 'offer', offer: null }, tab: 'offers', show: canWrite },
    { key: 'followup', icon: ListTodo, dialog: { kind: 'followup', followup: null }, tab: 'overview', show: canWrite },
    { key: 'renewal', icon: RefreshCcw, dialog: { kind: 'renewal', renewal: null }, tab: 'orders', show: canWrite },
    { key: 'trial', icon: FlaskConical, dialog: { kind: 'trial' }, tab: 'delivery', show: canWrite },
    { key: 'work', icon: Wrench, dialog: { kind: 'work', work: null }, tab: 'delivery', show: canWrite },
    { key: 'request', icon: Headset, dialog: { kind: 'request', request: null }, tab: 'requests', show: canWrite },
    { key: 'invoice', icon: FileText, dialog: { kind: 'invoice' }, tab: 'finance', show: canPay },
    { key: 'receipt', icon: Landmark, dialog: { kind: 'receipt' }, tab: 'finance', show: canPay },
    { key: 'file', icon: FileUp, dialog: { kind: 'file', file: null }, tab: 'files', show: canWrite },
  ]
  const meta = (icon: ReactNode, label: string, value: ReactNode) => (
    <span className="inline-flex items-center gap-1.5">
      {icon}
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </span>
  )
  const content: Record<Tab, ReactNode> = {
    overview: <OverviewTab customer={c} summary={summary} canWrite={canWrite} onEdit={() => setDialog({ kind: 'customer' })} onTab={go} open={setDialog} />,
    notes: <NotesTab customerId={c.id} canWrite={can(CRM_NOTE_WRITE_PERM)} />,
    meetings: <MeetingsTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
    offers: <OffersTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
    orders: <OrdersTab customerId={c.id} canWrite={canWrite} canApprove={canPay} open={setDialog} />,
    project: <ProjectTab customer={c} canWrite={canWrite} open={setDialog} />,
    delivery: <DeliveryTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
    finance: <FinanceTab customerId={c.id} summary={summary} open={setDialog} />,
    emails: <EmailsTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
    requests: <RequestsTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
    files: <FilesTab customerId={c.id} canWrite={canWrite} open={setDialog} />,
  }

  return (
    <>
      <PageHeader
        backTo="/customers/list"
        title={
          <span className="flex flex-wrap items-center gap-3">
            <CustomerAvatar name={c.name} className="size-11 text-base" />
            {c.name}
            <StageBadge stage={c.stage} />
            <DeliveryBadge delivery={c.delivery} />
            {canWrite ? <CategoryPicker customerId={c.id} categoryId={c.categoryId} /> : <CategoryBadge categoryId={c.categoryId} />}
          </span>
        }
        description={
          <span className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {meta(<User className="size-4" aria-hidden />, t('crm.fields.contact'), c.contactName)}
            {meta(<Users className="size-4" aria-hidden />, t('crm.fields.owner'), staffName(c.ownerId))}
            {meta(
              <FolderKanban className="size-4" aria-hidden />,
              t('crm.fields.project'),
              <Link to={`/projects/${c.projectId}`} className="hover:text-primary hover:underline">
                {projectName(c.projectId)}
              </Link>,
            )}
          </span>
        }
        actions={
          <>
            {canWrite ? (
              <Button variant="outline" className="border-success/40 text-success hover:bg-success/10 hover:text-success" onClick={() => void onStartMeeting()} disabled={start.isPending}>
                {start.isPending ? <Loader2 className="animate-spin" /> : <Play />}
                {t('crm.live.start')}
              </Button>
            ) : null}
            {activity.some((a) => a.show && tabs.includes(a.tab)) ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus />
                    {t('crm.activity')}
                    <ChevronDown />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-56">
                  {activity
                    .filter((a) => a.show && tabs.includes(a.tab))
                    .map((a) => (
                      <DropdownMenuItem
                        key={a.key}
                        onSelect={() => {
                          if (tab !== a.tab) go(a.tab)
                          setDialog(a.dialog)
                        }}
                      >
                        <a.icon />
                        {t(`crm.activityMenu.${a.key}`)}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {canWrite ? (
              <Button variant="outline" onClick={() => setDialog({ kind: 'email' })} disabled={!c.email}>
                <Mail />
                {t('crm.email.title')}
              </Button>
            ) : null}
            {canWrite ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t('crm.moreActions')}>
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setDialog({ kind: 'customer' })}>
                    <Pencil />
                    {t('common.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void onDelete()} className="text-destructive [&_svg]:text-destructive">
                    <Trash2 />
                    {t('common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />

      <Tabs value={tab} onValueChange={go} className="mb-4">
        <TabsList>
          {tabs.map((k) => (
            <TabsTrigger key={k} value={k}>
              {t(`crm.tabs.${k}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {content[tab]}

      <CrmDialogHost
        dialog={dialog}
        customer={c}
        onClose={() => setDialog(null)}
        onMeetingSaved={(id) => navigate(`/customers/${c.id}/meetings?m=${id}`, { replace: tab === 'meetings' })}
      />
    </>
  )
}
